import { WidgetID, WidgetModel, Aggregate } from './widget.model';
import PptxGenJS from 'pptxgenjs';
import {
  AggregateResponse,
  ChartSeriesResponse,
  GeographyResponse,
  ListResponse,
} from '../store/api/chart.api';
import { KwelloBarChartOptions, KwelloLineChartOptions } from './chart.model';
import { SupportedWidgetRenderType } from '../components/widgets/widget-utils/ppt-export/pptWidgetConfig';
import { ExportModel, WidgetExportModel } from './export.model';
import { QuestionModel } from './dataset.model';
import { PPTExportOptions, PPTFilter } from '../hooks/usePowerPointExport';
import { WidgetFactoryResult } from '../hooks/widgets';
import { FilterButtonFilterObject } from '../components/widgets/widget-subcomponents/DashboardWidgetFilterButton';

export interface PPTPosition {
  x?: number;
  y?: number;
  h?: number;
}

export interface PPTGenerateParameters {
  widget: WidgetModel;
  data: PPTWidgetData;
  chartOptions?: PPTExportChartOptions;
  summaryBar?: WidgetExportModel[]; // Optional summary bar for additional data
  question: QuestionModel | null; // Optional question context
  appliedFilter: PPTFilter;
  universalFilters?: FilterButtonFilterObject | undefined;
  isReport?: boolean; // Optional flag to indicate if this is a report view
  isAlignment?: boolean; // Optional flag to indicate if this is an alignment view
}

export interface MultiExportQuestionList {
  questionId: number;
  surveyTitle: string;
  questionText: string;
  isAlignment?: boolean;
  leftChartGrouping?: { [key: string]: unknown }[];
  rightChartGrouping?: { [key: string]: unknown }[];
}

export interface PPTGenerateSplitSlideParameters {
  leftWidget: WidgetModel;
  rightWidget: WidgetModel;
  summaryBar?: WidgetExportModel[]; // Optional summary bar for additional data
  leftWidgetData: PPTWidgetData;
  rightWidgetData: PPTWidgetData;
  question: MultiExportQuestionList; // Optional question context
  projectName?: string; // Optional project name for context
  appliedFilter: PPTFilter;
  isAlignment: boolean; // Optional flag to indicate if this is an alignment view
}

export interface PPTLegendOptions {
  x?: number; // starting x position
  y?: number; // y position below the chart
  boxSize?: number; // size of the colored box
  gap?: number; // spacing between items
  fontSize?: number; // font size for the labels
  fontColor?: string; // color of the label text
  fontFace?: string; // font face for the labels
}

export interface PPTLegendItem {
  label: string; // Label for the legend item
  color?: string; // Optional color for the legend item, if not provided will use default colors
}

export interface PPTChartData {
  name: string;
  labels: string[];
  values: number[];
  image?: string;
  color?: string; // Optional color for the chart series
  displayLabels?: string[]; // Per-point label text for PPTX post-processing (e.g., "32.2%")
}

export interface PPTSummaryBarRendererParameters {
  slide: PptxGenJS.Slide;
  pptx: PptxGenJS;
  summaryData?: Aggregate[];
  isAlignment?: boolean;
  position?: PPTPosition;
}

export interface PPTMultiQuestionSumamryBarRendererParameters
  extends PPTSummaryBarRendererParameters {
  question: MultiExportQuestionList;
  startDate: Date | string; // Optional start date for the summary bar
  endDate: Date | string; // Optional end date for the summary bar
  position?: PPTPosition;
}

export interface PPTSurveyQuestionRendererParameters {
  slide: PptxGenJS.Slide;
  question: QuestionModel | null;
  pptx: PptxGenJS;
}

export interface PPTMultiQuestionSurveyQuestionRendererParameters {
  slide: PptxGenJS.Slide;
  question: MultiExportQuestionList;
  pptx: PptxGenJS;
}

export interface PPTRendererParameters<T> {
  slide: PptxGenJS.Slide;
  pptx: PptxGenJS;
  data: T;
  rawData?: PPTWidgetData; // Optional raw data for additional context
  image?: string;
  summaryData?: AggregateResponse;
  chartOptions?: PPTExportChartOptions;
  widget?: WidgetModel; // Optional widget context
  overrideRenderOptions?: PptxGenJS.IChartOpts;
  position?: PPTPosition;
}

export interface SingleTileExportPPT {
  widget: WidgetModel;
  summaryBar: WidgetExportModel[];
  question: QuestionModel | null;
  options?: PPTExportOptions;
}

export interface PageExportPPT {
  widgets: ExportModel[];
  question: QuestionModel | null;
  summaryBar: (WidgetExportModel | null)[];
  options?: PPTExportOptions;
  pageTitle: string;
  pageName: string;
  widgetOptions: {
    key: number;
    value: {
      id: string;
      kwelloBarChartOptions?: KwelloBarChartOptions;
      kwelloLineChartOptions?: KwelloLineChartOptions;
    };
  }[];
}

export interface QuestionExportPPT {
  questions: MultiExportQuestionList[];
  leftChartJson: Partial<WidgetFactoryResult>;
  rightChartJson: Partial<WidgetFactoryResult>;
  summaryBar: Partial<WidgetFactoryResult>;
  oppositeLeftChart: Partial<WidgetFactoryResult>;
  oppositeRightChart: Partial<WidgetFactoryResult>;
  oppositeSummaryBar: Partial<WidgetFactoryResult>;
  apiUrl: string;
}

export type SankeyResponse = ChartSeriesResponse & { image: string };
export type WordcloudResponse = ListResponse & { image: string };

export type PPTWidgetData =
  | ChartSeriesResponse
  | SankeyResponse
  | ListResponse
  | AggregateResponse
  | GeographyResponse
  | WordcloudResponse;

export interface PPTExportChartOptions {
  barChartOptions?: KwelloBarChartOptions;
  lineChartOptions?: KwelloLineChartOptions;
}

export interface PPTSplitData {
  leftChart: ChartSeriesResponse;
  rightChart: ChartSeriesResponse;
  summaryBar: AggregateResponse;
  question?: QuestionModel;
}

export interface PPTProcessorParameters<T> {
  data: T;
  chartOptions?: PPTExportChartOptions;
  widget: WidgetModel; // Optional widget context for additional processing
  appliedFilter: PPTFilter;
}

export type PPTDataProcessor = Partial<
  Record<SupportedWidgetRenderType, (params: PPTProcessorParameters<any>) => PPTDataProcessorResult>
>;

export type PPTWidgetRenderer = Record<
  SupportedWidgetRenderType,
  (params: PPTRendererParameters<any>) => void
>;

export interface PPTImageResult {
  image: string;
}

export interface PPTWordcloudResult extends PPTImageResult {
  words: { text: string; value: number; id: string }[];
}

export interface PPTSankeyChartResult extends PPTImageResult {
  previousAlignment: Record<string, number>;
  latestAlignment: Record<string, number>;
}

export interface PPTGeographyRecord {
  country: string;
  value: number;
}

export type PPTDataProcessorResult =
  | PPTChartData[]
  | PPTSankeyChartResult
  | PPTWordcloudResult
  | PPTGeographyRecord[];

export enum PPT_WORKER_STATUS {
  PROGRESS = 'ppt-export-progress',
  ERROR = 'ppt-export-error',
  READY = 'ppt-export-ready',
  EXPORT_ABORTED = 'ppt-export-aborted',
}

export interface MultiQuestionWidgetResult<T> {
  data: T;
  widget: WidgetModel;
}

export interface PPTMultiQuestionExportData {
  question: MultiExportQuestionList;
  leftChart: MultiQuestionWidgetResult<ChartSeriesResponse>;
  rightChart: MultiQuestionWidgetResult<ChartSeriesResponse>;
  summaryBar: MultiQuestionWidgetResult<AggregateResponse>;
}

export interface PPTWorkerExportMessage {
  type: PPT_WORKER_STATUS;
  index: number;
  progress: number;
  data: PPTMultiQuestionExportData[];
  error: string;
  message?: string; // Added optional message property for EXPORT_ABORTED
}

export enum WizardStep {
  PowerpointExportChooseOption = 0,
  PowerpointExportSummary = 1,
  PowerpointExportQuestion = 2,
}

export interface ExportPptModel {
  visible: boolean;
  flyoutOpen: boolean;
  exportables: { [key: WidgetID]: Exportable };
  wizardStep: WizardStep;
  exportTotal: number;
  exportProgress: number;
  showProgressBar: boolean;
  exportRunningInBackground: boolean;
}

export interface Exportable {
  state: boolean;
  displayName: string;
  icon?: any;
}

export interface ToggleExportablePayload {
  id: string;
  state: boolean;
}
